// Input group component from shadcn

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Button } from '@/components/ui/button';
import { cn } from '@/utilities/cn';

function InputGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="input-group"
      className={cn(
        'group/input-group flex min-w-0 flex-wrap items-stretch rounded-lg border border-input bg-transparent transition-[color,box-shadow,border-color] focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-input/50 has-[textarea:disabled]:cursor-not-allowed has-[textarea:disabled]:bg-input/50 dark:has-[input:disabled]:bg-input/80 dark:has-[textarea:disabled]:bg-input/80',
        className
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  "flex shrink-0 items-center gap-1.5 text-muted-foreground [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        'inline-start': 'order-first pl-3 pr-1',
        'inline-end': 'order-last pl-1 pr-3',
        'block-start':
          'order-first basis-full justify-between border-b border-input px-3 py-2 text-sm',
        'block-end':
          'order-last basis-full justify-between border-t border-input px-3 py-2 text-sm',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
);

function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      {...props}
    />
  );
}

function InputGroupButton({
  className,
  variant = 'ghost',
  size = 'xs',
  ...props
}: React.ComponentProps<typeof Button> & {
  size?: 'xs' | 'icon-xs' | 'sm' | 'icon-sm';
}) {
  return (
    <Button
      data-slot="input-group-button"
      variant={variant}
      size={size}
      className={cn('shrink-0', className)}
      {...props}
    />
  );
}

function InputGroupInput({
  className,
  type,
  ...props
}: React.ComponentProps<'input'>) {
  return (
    <input
      type={type}
      data-slot="input-group-control"
      className={cn(
        'h-11 min-w-0 flex-1 basis-0 bg-transparent px-4 text-base outline-none placeholder:text-muted-foreground disabled:pointer-events-none md:text-sm',
        className
      )}
      {...props}
    />
  );
}

function InputGroupTextarea({
  className,
  ...props
}: React.ComponentProps<'textarea'>) {
  return (
    <textarea
      data-slot="input-group-control"
      className={cn(
        'field-sizing-content min-h-24 min-w-0 flex-1 basis-full resize-none bg-transparent px-4 py-3 text-base outline-none placeholder:text-muted-foreground disabled:pointer-events-none md:text-sm',
        className
      )}
      {...props}
    />
  );
}

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="input-group-text"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
};
